import React, { useState } from 'react';
import { NavLink, Outlet, useLocation, Navigate } from 'react-router-dom';
import { 
  LayoutDashboard, Building2, Hotel, MessageSquare, Users, Settings,
  History, Sidebar, Tag, User, Lock, ChevronRight, Scale, Key, Trash2, Timer, Clock, Shield, ShieldCheck, Link2, Mail, Server, ClipboardList, Megaphone
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * Enterprise Settings Layout
 * Two-panel layout: Left navigation + Right content
 * Role-based access: Admin sees all, User sees only Profile & Password
 */

const settingsSections = [
  {
    title: 'System',
    adminOnly: true,
    items: [
      { id: 'modules', path: '/settings/modules', label: 'Module Management', icon: LayoutDashboard },
      { id: 'custom-fields', path: '/settings/custom-fields', label: 'Account Form Builder', icon: Settings },
    ]
  },
  {
    title: 'Module Settings',
    adminOnly: true,
    items: [
      { id: 'dashboard', path: '/settings/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'hotel-onboarded', path: '/settings/hotel-onboarded', label: 'Hotel Onboarded', icon: Building2 },
      { id: 'properties', path: '/settings/properties', label: 'Properties', icon: Hotel },
      { id: 'hotel-lifecycle', path: '/settings/hotel-lifecycle', label: 'Hotel Lifecycle', icon: Building2 },
      { id: 'broadcast', path: '/settings/broadcast', label: 'Broadcast', icon: MessageSquare },
      { id: 'chatbot', path: '/settings/chatbot', label: 'Chatbot', icon: MessageSquare },
      { id: 'whatsapp', path: '/settings/whatsapp', label: 'WhatsApp', icon: MessageSquare },
      { id: 'analytics', path: '/settings/analytics', label: 'Analytics', icon: LayoutDashboard },
    ]
  },
  {
    title: 'Administration',
    adminOnly: true,
    items: [
      { id: 'audit-history', path: '/settings/audit-history', label: 'Audit History', icon: History },
      { id: 'users', path: '/settings/users', label: 'User Management', icon: Users },
      { id: 'sidebar', path: '/settings/sidebar', label: 'Sidebar Visibility', icon: Sidebar },
      { id: 'labels', path: '/settings/labels', label: 'Labels Management', icon: Tag },
      { id: 'api-keys', path: '/settings/api-keys', label: 'API Keys', icon: Key },
      { id: 'data-management', path: '/settings/data-management', label: 'Data Management', icon: Trash2 },
      { id: 'checklist-monitor', path: '/settings/checklist-monitor', label: 'Checklist Monitoring', icon: Timer },
      { id: 'ota-timers', path: '/settings/ota-timers', label: 'OTA Timer Limits', icon: Clock },
      { id: 'approval-timeout', path: '/settings/approval-timeout', label: 'Approval Timeout', icon: Shield },
      { id: 'mapping', path: '/settings/mapping', label: 'Property Mapping', icon: Link2 },
      { id: 'qc-settings', path: '/settings/qc-settings', label: 'QC Settings', icon: ShieldCheck },
      { id: 'onboarding-form-settings', path: '/settings/onboarding-form-settings', label: 'Onboarding Form Settings', icon: ClipboardList },
      { id: 'reports-config', path: '/settings/reports-config', label: 'Reports Configuration', icon: LayoutDashboard },
      { id: 'section-access', path: '/settings/section-access', label: 'Account Section Access', icon: Lock },
    ]
  },
  {
    title: 'Integrations',
    adminOnly: true,
    items: [
      { id: 'gmail', path: '/settings/gmail', label: 'Gmail Integration', icon: Mail },
      { id: 'meta-lead-ads', path: '/settings/integrations/meta', label: 'Meta Lead Ads', icon: Megaphone },
      { id: 'superhost-admin', path: '/settings/superhost-admin', label: 'Superhost Admin Integration', icon: Server },
      { id: 'email-logs', path: '/settings/email-logs', label: 'Email Logs', icon: History },
    ]
  },
  {
    title: 'Account Settings',
    adminOnly: false,
    items: [
      { id: 'profile', path: '/settings/profile', label: 'Profile Settings', icon: User },
      { id: 'password', path: '/settings/password', label: 'Change Password', icon: Lock },
    ]
  },
  {
    title: 'General',
    adminOnly: true,
    items: [
      { id: 'general', path: '/settings/general', label: 'System Settings', icon: Settings },
    ]
  },
  {
    title: 'Legal',
    adminOnly: false,
    items: [
      { id: 'legal', path: '/settings/legal', label: 'Legal & Compliance', icon: Scale },
    ]
  }
];

const SettingsLayout = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  // Redirect to profile if non-admin accesses admin-only routes
  const currentPath = location.pathname;
  const isAdminOnlyPath = settingsSections
    .filter(s => s.adminOnly)
    .flatMap(s => s.items.map(i => i.path))
    .some(p => currentPath.startsWith(p));

  if (!isAdmin && isAdminOnlyPath) {
    return <Navigate to="/settings/profile" replace />;
  }

  // Filter sections based on role
  const visibleSections = settingsSections.filter(section => {
    if (section.adminOnly && !isAdmin) return false;
    return true;
  });

  return (
    <div className="flex h-[calc(100vh-140px)] gap-6" data-testid="settings-layout">
      {/* Left Navigation Panel */}
      <div className="w-60 shrink-0 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white shrink-0">
          <h2 className="font-bold text-gray-800 text-lg flex items-center gap-2">
            <Settings size={20} className="text-teal-600" />
            Settings
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {isAdmin ? 'Administrator Access' : 'User Access'}
          </p>
        </div>
        
        <nav className="p-3 space-y-4 overflow-y-auto flex-1 min-h-0">
          {visibleSections.map((section, idx) => (
            <div key={idx}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
                {section.title}
              </h3>
              <div className="space-y-0.5">
                {section.items.map(item => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path || 
                                   location.pathname.startsWith(item.path + '/');
                  return (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-teal-50 text-teal-700 shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      data-testid={`settings-nav-${item.id}`}
                    >
                      <Icon size={18} className={isActive ? 'text-teal-600' : 'text-gray-400'} />
                      <span className="flex-1">{item.label}</span>
                      {isActive && <ChevronRight size={16} className="text-teal-400" />}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Right Content Panel */}
      <div className="flex-1 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 overflow-y-auto h-full">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default SettingsLayout;
